/**
 * Thin Candles Plugin for LightweightCharts v5 (Planning chart)
 *
 * lightweight-charts couples the native candlestick body width to the time
 * scale's barSpacing (wider spacing => fatter body), with no public override.
 * On the planning chart the fat bodies sit on top of the per-candle profiles
 * and hide them. This plugin draws the candle bodies ourselves at a FIXED pixel
 * width, decoupled from barSpacing, so the price series can keep wide spacing
 * (separation) while the bodies stay thin and the profiles remain visible.
 *
 * Usage: hide the native candlestick body (set its colors transparent) and
 * attach this plugin; it reads the series' own OHLC data (series.data()) and
 * paints thin candles above everything (zOrder 'top').
 */

class ThinCandlesRenderer {
  constructor(store, series, chart) {
    this.store = store;
    this.series = series;
    this.chart = chart;
  }

  draw(target) {
    const { series, chart } = this;
    if (!series || !chart || typeof series.data !== 'function') return;
    const data = series.data();
    if (!data || data.length === 0) return;

    target.useBitmapCoordinateSpace((scope) => {
      const ctx = scope.context;
      const { horizontalPixelRatio: hpr, verticalPixelRatio: vpr } = scope;
      const ts = chart.timeScale();
      const range = ts.getVisibleRange ? ts.getVisibleRange() : null;

      const bodyW = Math.max(1, Math.round((this.store.widthPx || 3) * hpr));
      const wickW = Math.max(1, Math.round(hpr));
      const up = this.store.upColor || '#22c55e';
      const down = this.store.downColor || '#ef4444';

      for (let i = 0; i < data.length; i++) {
        const c = data[i];
        if (!c || c.open == null || c.close == null) continue; // skip whitespace/line points
        if (range && (c.time < range.from || c.time > range.to)) continue;

        const x = ts.timeToCoordinate(c.time);
        if (x === null) continue;
        const cx = x * hpr;

        const yH = series.priceToCoordinate(c.high);
        const yL = series.priceToCoordinate(c.low);
        const yO = series.priceToCoordinate(c.open);
        const yC = series.priceToCoordinate(c.close);
        if (yH === null || yL === null || yO === null || yC === null) continue;

        const color = c.close >= c.open ? up : down;
        ctx.fillStyle = color;

        // Wick (single vertical line, centered)
        const wickX = Math.round(cx - wickW / 2);
        const wickTop = Math.round(yH * vpr);
        const wickH = Math.max(1, Math.round((yL - yH) * vpr));
        ctx.fillRect(wickX, wickTop, wickW, wickH);

        // Body (fixed-width rect)
        const bodyX = Math.round(cx - bodyW / 2);
        const bodyTop = Math.round(Math.min(yO, yC) * vpr);
        const bodyH = Math.max(1, Math.round(Math.abs(yC - yO) * vpr));
        ctx.fillRect(bodyX, bodyTop, bodyW, bodyH);
      }
    });
  }
}

class ThinCandlesPrimitiveView {
  constructor(store, series, chart) {
    this._renderer = new ThinCandlesRenderer(store, series, chart);
  }
  zOrder() { return 'top'; }            // draw above the (transparent) native body
  renderer() { return this._renderer; }
}

class ThinCandlesPrimitive {
  constructor(store, series, chart) {
    this._view = new ThinCandlesPrimitiveView(store, series, chart);
    this._requestUpdate = null;
    this._rafPending = false;
  }
  attached({ requestUpdate }) { this._requestUpdate = requestUpdate; }
  detached() { this._requestUpdate = null; this._rafPending = false; }
  updateAllViews() { return this._view; }
  paneViews() { return [this._view]; }
  scheduleUpdate() {
    if (this._requestUpdate && !this._rafPending) {
      this._rafPending = true;
      requestAnimationFrame(() => {
        this._rafPending = false;
        if (this._requestUpdate) this._requestUpdate();
      });
    }
  }
}

export class ThinCandlesPlugin {
  constructor(chart, series) {
    this.chart = chart;
    this.series = series;
    this.store = { widthPx: 3, upColor: '#22c55e', downColor: '#ef4444' };
    this.primitive = null;
    this.enabled = false;
  }

  enable() {
    if (this.enabled) return;
    this.enabled = true;
    this.primitive = new ThinCandlesPrimitive(this.store, this.series, this.chart);
    this.series.attachPrimitive(this.primitive);
  }

  disable() {
    if (this.primitive) {
      try { this.series.detachPrimitive(this.primitive); } catch (e) { /* ignore */ }
      this.primitive = null;
    }
    this.enabled = false;
  }

  isEnabled() { return this.enabled; }

  setOptions({ widthPx, upColor, downColor } = {}) {
    if (widthPx != null) this.store.widthPx = widthPx;
    if (upColor) this.store.upColor = upColor;
    if (downColor) this.store.downColor = downColor;
    if (this.primitive) this.primitive.scheduleUpdate();
  }
}

export default ThinCandlesPlugin;
